import type {
  JournalListItemDTO,
  JournalOrderRequestDTO,
  JournalOrderResponseDTO,
} from '~/shared/api/client/journals/journals.dto';
import { CLIENT_API_JOURNAL_ORDERS_URL } from '~/shared/api/client/journals/journals.const';
import { customFetch } from '~/shared/lib/customFetch';
import { isValidEmail } from '~/shared/utils/isValidEmail';
import {
  JOURNAL_ORDER_ERRORS,
  JOURNAL_ORDER_FIELDS,
  type JournalOrderFieldName,
} from './journalOrder.const';

type JournalOrderValues = Record<JournalOrderFieldName, string>;

/** Enough digits for a Russian number with or without the country code */
const MIN_PHONE_DIGITS = 10;

const createValues = (): JournalOrderValues =>
  Object.fromEntries(
    JOURNAL_ORDER_FIELDS.map((field) => [field.name, '']),
  ) as JournalOrderValues;

const countDigits = (value: string) => value.replace(/\D/g, '').length;

/**
 * The order form: values, the fields to mark red and the send itself.
 *
 * Validation runs on submit and not while typing — the layout has no error
 * texts under the fields, so the only way to say anything is the red border,
 * and drawing it under someone's hands as they fill the field in is noise.
 * A field clears its own mark as soon as it changes
 */
export function useJournalOrderForm(getIssue: () => JournalListItemDTO) {
  const values = reactive<JournalOrderValues>(createValues());
  const invalidFields = ref<JournalOrderFieldName[]>([]);
  const error = ref<string | null>(null);
  const isSubmitting = ref(false);

  const setValue = (name: JournalOrderFieldName, value: string) => {
    values[name] = value;
    invalidFields.value = invalidFields.value.filter((item) => item !== name);
    error.value = null;
  };

  const isInvalid = (name: JournalOrderFieldName) =>
    invalidFields.value.includes(name);

  /**
   * Fills `invalidFields` and returns the one line shown under the form. The
   * empty-field check comes first: pointing at a malformed email while three
   * fields above are still empty would send the buyer up and down the form
   */
  const validate = (): string | null => {
    const empty = JOURNAL_ORDER_FIELDS.filter(
      (field) => field.required && !values[field.name].trim(),
    ).map((field) => field.name);

    if (empty.length) {
      invalidFields.value = empty;

      return JOURNAL_ORDER_ERRORS.required;
    }

    if (!isValidEmail(values.email.trim())) {
      invalidFields.value = ['email'];

      return JOURNAL_ORDER_ERRORS.email;
    }

    if (countDigits(values.phone) < MIN_PHONE_DIGITS) {
      invalidFields.value = ['phone'];

      return JOURNAL_ORDER_ERRORS.phone;
    }

    invalidFields.value = [];

    return null;
  };

  const buildPayload = (captchaToken: string): JournalOrderRequestDTO => ({
    journal: getIssue().id,
    full_name: values.full_name.trim(),
    company: values.company.trim(),
    business_field: values.business_field.trim(),
    position: values.position.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
    promo_code: values.promo_code.trim(),
    comment: values.comment.trim(),
    captcha_token: captchaToken,
  });

  /** true when the order went through — the modal then shows the thank-you */
  const submit = async (captchaToken: string): Promise<boolean> => {
    if (isSubmitting.value) return false;

    const message =
      validate() ?? (captchaToken ? null : JOURNAL_ORDER_ERRORS.captcha);

    if (message) {
      error.value = message;

      return false;
    }

    isSubmitting.value = true;

    try {
      /*
       * The sum is not in the payload: the backend derives it from the issue
       * and the promo code, so it cannot be tampered with (ADR 0001, decision 8)
       */
      await customFetch<JournalOrderResponseDTO>(
        CLIENT_API_JOURNAL_ORDERS_URL,
        {
          method: 'POST',
          body: buildPayload(captchaToken),
        },
      );

      return true;
    } catch {
      error.value = JOURNAL_ORDER_ERRORS.request;

      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  return { values, error, isSubmitting, isInvalid, setValue, submit };
}
